import React, { useEffect } from 'react';
import { motion } from 'framer-motion';
import { XIcon } from 'lucide-react';
import { QualifyingLoan } from '../types/commission';
import { formatCurrency, formatDate } from '../utils/commission';
interface QualifyingLoansDrawerProps {
  title: string;
  windowLabel: string | null;
  loans: QualifyingLoan[];
  onClose: () => void;
}
export function QualifyingLoansDrawer({
  title,
  windowLabel,
  loans,
  onClose
}: QualifyingLoansDrawerProps) {
  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);
  return <>
      <motion.div role="presentation" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} className="fixed inset-0 z-40 bg-slate-900/40" onClick={onClose} data-id="element-441" data-mp="3m9yfxmu87u3j" />
      <motion.section role="dialog" aria-modal="true" aria-labelledby="qualifying-loans-title" initial={{
      x: '100%'
    }} animate={{
      x: 0
    }} transition={{
      duration: 0.22,
      ease: 'easeOut'
    }} className="fixed inset-y-0 right-0 z-50 flex w-full max-w-2xl flex-col bg-white shadow-2xl" data-id="element-442" data-mp="3w3xhajmh79uf">
        <header className="flex items-start justify-between gap-4 border-b border-slate-200 px-6 py-5" data-id="element-443" data-mp="krbuegigbn2l">
          <div data-id="element-444" data-mp="1hhb08377w7hh">
            <h2 id="qualifying-loans-title" className="text-base font-bold text-slate-900" data-id="element-445" data-mp="1hfrie85wxodl">
              Qualifying loans
            </h2>
            <p className="mt-0.5 text-sm text-slate-600" data-id="element-446" data-mp="1dhtt75c572a7">{title}</p>
            {windowLabel ? <p className="mt-1 text-xs font-medium text-slate-400" data-id="element-447" data-mp="18mwi7svayiuo">{windowLabel}</p> : null}
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="rounded-lg p-1.5 text-slate-400 transition hover:bg-slate-100 hover:text-slate-700" data-id="element-448" data-mp="13njyvi3n3twu">
            <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-449" data-mp="2fau54phnujpt" />
          </button>
        </header>
        <div className="flex-1 overflow-auto" data-id="element-450" data-mp="2k9ngocde00nc">
          <table className="w-full min-w-[560px] text-left text-sm" data-id="element-451" data-mp="2k8c5vslixsmq">
            <thead className="sticky top-0" data-id="element-452" data-mp="26is7t02w1jjr">
              <tr className="border-b border-slate-200 bg-slate-50 text-xs font-semibold uppercase tracking-wider text-slate-500" data-id="element-453" data-mp="26i0bfn1gvhsf">
                <th scope="col" className="px-6 py-3" data-id="element-454" data-mp="1wk2nz1rflp0v">
                  Borrower
                </th>
                <th scope="col" className="px-4 py-3" data-id="element-455" data-mp="21iw28wejlk23">
                  Loan #
                </th>
                <th scope="col" className="px-4 py-3" data-id="element-456" data-mp="1njnydq7fpnrk">
                  Funded
                </th>
                <th scope="col" className="px-4 py-3" data-id="element-457" data-mp="1rkpzydotqbwp">
                  Pay period
                </th>
                <th scope="col" className="px-6 py-3 text-right" data-id="element-458" data-mp="3hwvzfzm06vln">
                  Loan amount
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100" data-id="element-459" data-mp="3ly7pu7cpbkuw">
              {loans.length === 0 ? <tr data-id="element-460" data-mp="1abx6xcks6fbq">
                  <td colSpan={5} className="px-6 py-12 text-center text-sm text-slate-500" data-id="element-461" data-mp="1a9b4frcqyq0d">
                    No funded loans in this window yet.
                  </td>
                </tr> : loans.map(loan => <tr key={`${loan.payPeriodName}-${loan.id}`} data-id="element-462" data-mp="1f9yqrzq16io9">
                    <th scope="row" className="px-6 py-3 font-semibold" data-id="element-463" data-mp="1tzehjfpkao3y">
                      <a href={`#/loans/${loan.id}`} onClick={event => event.preventDefault()} className="text-brand-700 underline decoration-brand-200 decoration-2 underline-offset-2 hover:text-brand-900" data-id="element-464" data-mp="1u1he6il7zfhn">
                        {loan.borrower}
                      </a>
                    </th>
                    <td className="px-4 py-3 tabular-nums text-slate-500" data-id="element-465" data-mp="8oedw0hca7sr">{loan.id}</td>
                    <td className="px-4 py-3 tabular-nums text-slate-700" data-id="element-466" data-mp="3p1nx6v68kly">{formatDate(loan.fundedDate)}</td>
                    <td className="px-4 py-3 text-slate-700" data-id="element-467" data-mp="63yf9rwqjm06">{loan.payPeriodName}</td>
                    <td className="px-6 py-3 text-right tabular-nums text-slate-900" data-id="element-468" data-mp="a1nt57a17auh">{formatCurrency(loan.amount)}</td>
                  </tr>)}
            </tbody>
          </table>
        </div>
      </motion.section>
    </>;
}